import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Radio,
  TextField,
  FormControl,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import Snackbar from "@mui/material/Snackbar";
import Alert, { AlertProps } from "@mui/material/Alert";
import { useDispatch, useSelector } from "react-redux";
import {
  addPackingPlantAddMinorIngredient,
  addProcessingPackagingPlantsDiff,
  addSourceBePartOfOptimization,
  createrFolderOrScenarioError,
  createFolder,
  displayFolderList,
  displayFolderListError,
  displayFolderListSuccess,
  inputUploadFolderId,
  openErrorDialog,
  selectedFolderListSuccess,
} from "../../../Redux/Actions/InputData";
import {
  API_BASE_URL,
  LONG_CTREATE_GETALLFOLDERS_API,
} from "../../../Utils/Config";
import { getRequest } from "../../../Services/AxiosConfiguration";
import {
  getUserModalPreferences,
  saveUserModalPreferences,
  LONG_TERM_SCENARIO,
} from "../../../Services/ModalPreferenceService";
import type { LongTermScenarioPreferences } from "../../../Types/ModalPreferences";
import PlusIcon from "../../../Assests/plus-icon.svg";
import "./style.css";
import LongTermUploadContent from "./LongTermUploadContent";

type ScenarioSelection = "create" | "view";

const BootstrapDialog = styled(Dialog)(({ theme }: any) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
    maxHeight: "70vh",
    overflowY: "auto",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface LongTermScenarioSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user chooses to view existing scenario */
  onViewExistingScenario: () => void;
  /** Called when folder/scenario should be created/selected, keeping existing backend flow */
  handleCreateNewFolder: (data: any) => void;
  /** Called when user presses Go back to return to the previous landing modal */
  handleGoBack: () => void;
  /** Newly created folder/scenario IDs for inline upload */
  createdFolderId?: any;
  createdScenarioId?: any;
  /** Called when folder selection changes or Reset is clicked, to hide upload section */
  onClearCreatedFolder?: () => void;
}

const LongTermScenarioSelectionModal: React.FC<
  LongTermScenarioSelectionModalProps
> = ({
  isOpen,
  onClose,
  onViewExistingScenario,
  handleCreateNewFolder,
  handleGoBack,
  createdFolderId,
  createdScenarioId,
  onClearCreatedFolder,
}) => {
  const dispatch: any = useDispatch();

  const userData = useSelector(
    (state: any) => state.InputDataReducers.apiUserData,
  );

  const getIsProcessingPackagingPlantsDiff = useSelector(
    (state: any) => state.InputDataReducers.isProcessingPackagingPlantsDiff,
  );

  const getIsSourceBePartOfOptimization = useSelector(
    (state: any) => state.InputDataReducers.isSourceBePartOfOptimization,
  );

  const getIsPackingPlantAddMinorIngredient = useSelector(
    (state: any) => state.InputDataReducers.isPackingPlantAddMinorIngredient,
  );

  const [selection, setSelection] = useState<ScenarioSelection>("create");

  const [processingPlantsCount, setProcessingPlantsCount] = useState<any>(0);
  const [packagingPlantsCount, setPackagingPlantsCount] = useState<any>(0);
  const [plantClosure, setPlantClosure] = useState(false);

  const [isProcessingPackagingPlantsDiff, setProcessingPackagingPlantsDiff] =
    useState<any>(getIsProcessingPackagingPlantsDiff);
  const [isSourceBePartOfOptimization, setSourceBePartOfOptimization] =
    useState<any>(getIsSourceBePartOfOptimization);
  const [isPackingPlantAddMinorIngredient, setPackingPlantAddMinorIngredient] =
    useState<any>(getIsPackingPlantAddMinorIngredient);

  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const [moduleForUpload, setModuleForUpload] = useState<any>("");
  const [folderOptions, setFolderOptions] = useState<any[]>([]);
  const [selectFolder, setSelectFolder] = useState<any>({});
  const [isCreateNewFolder, setIsCreateNewFolder] = useState(false);
  const [newScenarioValue, setNewScenarioValue] = useState("");
  const [newFolderValue, setNewFolderValue] = useState("");

  const createrFolderOrScenarioErrorMsg = useSelector(
    (state: any) => state.InputDataReducers.createrFolderOrScenarioError,
  );

  const dialogContentRef = useRef<HTMLDivElement>(null);
  const pendingFolderIdFromPrefsRef = useRef<number | undefined>(undefined);
  const pendingFolderNameFromPrefsRef = useRef<string | undefined>(undefined);
  const pendingScenarioNameFromPrefsRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isOpen && userData?.userId) {
      getUserModalPreferences(LONG_TERM_SCENARIO, userData.userId).then(
        (prefs) => {
          if (prefs) {
            setSelection(prefs.selection);
            setProcessingPackagingPlantsDiff(
              prefs.isProcessingPackagingPlantsDiff,
            );
            setSourceBePartOfOptimization(prefs.isSourceBePartOfOptimization);
            setPackingPlantAddMinorIngredient(
              prefs.isPackingPlantAddMinorIngredient,
            );
            setPlantClosure(prefs.plantClosure ?? false);
            setProcessingPlantsCount(prefs.processingPlantsCount ?? 0);
            setPackagingPlantsCount(prefs.packagingPlantsCount ?? 0);
            if (prefs.scenarioName) {
              pendingScenarioNameFromPrefsRef.current = prefs.scenarioName;
              setTimeout(() => setNewScenarioValue(prefs.scenarioName!), 0);
            }
            dispatch(
              addProcessingPackagingPlantsDiff(
                prefs.isProcessingPackagingPlantsDiff,
              ),
            );
            dispatch(
              addSourceBePartOfOptimization(prefs.isSourceBePartOfOptimization),
            );
            dispatch(
              addPackingPlantAddMinorIngredient(
                prefs.isPackingPlantAddMinorIngredient,
              ),
            );
            if (prefs.selectedFolderId) {
              pendingFolderIdFromPrefsRef.current = prefs.selectedFolderId;
            }
            if (prefs.folderName) {
              pendingFolderNameFromPrefsRef.current = prefs.folderName;
            }
          }
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const pendingId = pendingFolderIdFromPrefsRef.current;
    const pendingName = pendingFolderNameFromPrefsRef.current;
    const pendingScenario = pendingScenarioNameFromPrefsRef.current;
    if ((pendingId || pendingName) && folderOptions?.length > 0) {
      const option = pendingId
        ? folderOptions.find((o: any) => String(o.id) === String(pendingId))
        : folderOptions.find((o: any) => {
            if (!pendingName) return false;
            const optFolder = String(o.folder ?? "").trim();
            const prefFolder = String(pendingName).trim();
            return (
              optFolder === prefFolder ||
              optFolder.endsWith("_" + prefFolder) ||
              optFolder.includes(prefFolder)
            );
          });
      if (option) {
        setSelectFolder(option);
        dispatch(selectedFolderListSuccess(option));
        dispatch(inputUploadFolderId(option.id));
      }
      pendingFolderIdFromPrefsRef.current = undefined;
      pendingFolderNameFromPrefsRef.current = undefined;
    }
    if (pendingScenario) {
      setNewScenarioValue(pendingScenario);
      pendingScenarioNameFromPrefsRef.current = undefined;
    }
  }, [folderOptions, dispatch, moduleForUpload]);

  useEffect(() => {
    if (createdFolderId && createdScenarioId && selection === "create") {
      setTimeout(() => {
        dialogContentRef.current?.scrollTo({
          top: dialogContentRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [createdFolderId, createdScenarioId, selection]);

  useEffect(() => {
    if (isProcessingPackagingPlantsDiff === false) {
      setPackingPlantAddMinorIngredient(false);
      dispatch(addPackingPlantAddMinorIngredient(false));
    }
  }, [isProcessingPackagingPlantsDiff, dispatch]);

  useEffect(() => {
    // derive module key from current Nakshatra radio selections
    if (
      isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module1");
    } else if (
      isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module2");
    } else if (
      !isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module3");
    } else if (
      !isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module4");
    } else if (
      isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module5");
    } else if (
      isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      setModuleForUpload("module6");
    }
  }, [
    isProcessingPackagingPlantsDiff,
    isSourceBePartOfOptimization,
    isPackingPlantAddMinorIngredient,
  ]);

  useEffect(() => {
    if (selection === "create" && moduleForUpload) {
      callFolderFilterApi(moduleForUpload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, moduleForUpload]);

  useEffect(() => {
    // Don't clear when restoring from prefs - pending refs indicate we're applying saved preferences
    if (
      pendingFolderIdFromPrefsRef.current !== undefined ||
      pendingFolderNameFromPrefsRef.current !== undefined ||
      pendingScenarioNameFromPrefsRef.current !== undefined
    ) {
      return;
    }
    setSelectFolder({});
    setNewScenarioValue("");
    setNewFolderValue("");
    onClearCreatedFolder?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProcessingPackagingPlantsDiff,
    isSourceBePartOfOptimization,
    isPackingPlantAddMinorIngredient,
  ]);

  const callFolderFilterApi = async (moduleKey: string) => {
    dispatch(displayFolderList());
    const urls = API_BASE_URL + LONG_CTREATE_GETALLFOLDERS_API;
    const header = {
      accept: "application/json",
      "Content-Type": "application/json",
    };

    await getRequest(urls, { ModuleKey: moduleKey }, header)
      .then((response) => {
        if (response.status === 200 && response.data) {
          const sorted = response.data?.data.sort((a: any, b: any) =>
            a?.folder.localeCompare(b?.folder),
          );
          setFolderOptions(sorted);
          dispatch(displayFolderListSuccess(response.data?.data));
        }
      })
      .catch((error) => {
        dispatch(openErrorDialog(error));
        dispatch(displayFolderListError(error.message));
      });
  };

  const handleSelectionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value as ScenarioSelection;
    setSelection(value);
  };

  const handleChangeSelectLine = (e: any, index: number) => {
    e.preventDefault();
    localStorage.setItem("selectedUsecase", "Long Term Analysis");

    const setValues = JSON.parse(e.target.value);
    if (index === 0) {
      setProcessingPackagingPlantsDiff(setValues);
      dispatch(addProcessingPackagingPlantsDiff(setValues));
      dispatch(addSourceBePartOfOptimization(true));
      if (setValues === false) {
        setPackingPlantAddMinorIngredient(false);
        dispatch(addPackingPlantAddMinorIngredient(false));
      }
      if (setValues === false) {
        setPackagingPlantsCount(0);
        setProcessingPlantsCount(0);
      }
      if (plantClosure) {
        setPlantClosure(false);
        setPackagingPlantsCount(0);
        setProcessingPlantsCount(0);
      }
    } else if (index === 1) {
      setSourceBePartOfOptimization(setValues);
      dispatch(addSourceBePartOfOptimization(setValues));
      if (setValues === false && !isProcessingPackagingPlantsDiff) {
        setPackingPlantAddMinorIngredient(false);
        dispatch(addPackingPlantAddMinorIngredient(false));
      }
      if (plantClosure) {
        setPlantClosure(false);
        setPackagingPlantsCount(0);
        setProcessingPlantsCount(0);
      }
    } else if (index === 2) {
      setPackingPlantAddMinorIngredient(setValues);
      dispatch(addPackingPlantAddMinorIngredient(setValues));
    }
  };

  const handleCheckEvaluate = (e: any) => {
    setPlantClosure(e.target.checked);
    if (!e.target.checked) {
      setPackagingPlantsCount(0);
      setProcessingPlantsCount(0);
    }
  };

  const handleChangeSelectFolder = (e: any) => {
    const val = e.target.value;
    if (!val || val === "") {
      setSelectFolder({});
      onClearCreatedFolder?.();
      return;
    }
    const option = folderOptions?.find(
      (o: any) => String(o.id) === String(val),
    );
    if (option) {
      setSelectFolder(option);
      localStorage.setItem("selectedCGOFolderId", option.id);
      dispatch(selectedFolderListSuccess(option));
      dispatch(inputUploadFolderId(option.id));
    }
    onClearCreatedFolder?.();
  };

  const handleFolderChange = (e: any) => {
    const { value } = e.target;
    if (value) {
      setNewFolderValue(value);
    } else {
      setNewFolderValue("");
    }
  };

  const handleScenarioChange = (e: any) => {
    const { value } = e.target;
    if (value) {
      setNewScenarioValue(value);
    } else {
      setNewScenarioValue("");
    }
  };

  const handleOpenCreateFolder = () => {
    dispatch(createFolder());
    setIsCreateNewFolder(true);
    onClearCreatedFolder?.();
  };

  const isFolderAndScenarioReady = isCreateNewFolder
    ? Boolean(newFolderValue?.trim() && newScenarioValue?.trim())
    : Boolean(selectFolder?.id && newScenarioValue?.trim());

  const handleBackFromFolderSection = () => {
    if (isCreateNewFolder || Object.keys(selectFolder)?.length !== 0) {
      dispatch(createrFolderOrScenarioError(""));
      setSelectFolder("");
      setNewScenarioValue("");
      setNewFolderValue("");
      setIsCreateNewFolder(false);
      onClearCreatedFolder?.();
    }
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  const isNakshatraCombinationValid = () => {
    if (
      isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    if (
      isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    if (
      isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    if (
      isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    if (
      !isProcessingPackagingPlantsDiff &&
      isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    if (
      !isProcessingPackagingPlantsDiff &&
      !isSourceBePartOfOptimization &&
      !isPackingPlantAddMinorIngredient
    ) {
      return true;
    }
    return false;
  };

  const saveCurrentPreferences = () => {
    const prefs: LongTermScenarioPreferences = {
      selection,
      isProcessingPackagingPlantsDiff,
      isSourceBePartOfOptimization,
      isPackingPlantAddMinorIngredient,
      plantClosure,
      processingPlantsCount: Number(processingPlantsCount) || 0,
      packagingPlantsCount: Number(packagingPlantsCount) || 0,
      selectedFolderId: selectFolder?.id,
      folderName: selectFolder?.folder ?? newFolderValue,
      scenarioName: newScenarioValue || null,
      lastModuleKey: moduleForUpload,
    };
    saveUserModalPreferences(LONG_TERM_SCENARIO, userData?.userId, prefs);
  };

  const handleFolderProceed = () => {
    if (!isNakshatraCombinationValid()) {
      setSnackbar({
        children:
          "Please try to give a valid senarios from the selected radios",
        severity: "error",
      });
      return;
    }
    saveCurrentPreferences();

    const inputCapacityModalData = {
      isProcessing: isProcessingPackagingPlantsDiff,
      isSourcing: isSourceBePartOfOptimization,
      isGradient: isPackingPlantAddMinorIngredient,
      isClosure: plantClosure,
      countofProcessingPlants: processingPlantsCount,
      countofPackagingPlants: packagingPlantsCount,
    };

    if (isCreateNewFolder) {
      handleCreateNewFolder({
        scenario: newScenarioValue,
        folder: moduleForUpload + "_" + newFolderValue,
        ...inputCapacityModalData,
        isCreateNewFolder: isCreateNewFolder,
        createdBy: userData?.userId,
        fromCombinedModal: true,
      });
    } else {
      handleCreateNewFolder({
        scenario: newScenarioValue,
        folder_id: selectFolder?.id || 0,
        ...inputCapacityModalData,
        isCreateNewFolder: isCreateNewFolder,
        createdBy: userData?.userId,
        fromCombinedModal: true,
      });
    }
  };

  const handleProceed = () => {
    if (selection === "view") {
      saveCurrentPreferences();
      onViewExistingScenario();
    }
  };

  const handleCloseWithSave = () => {
    saveCurrentPreferences();
    onClose();
  };

  return (
    <div>
      <BootstrapDialog
        onClose={handleCloseWithSave}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
        fullWidth
        maxWidth="md"
      >
        <div className="modal-header">
          <Grid container spacing={1}>
            <Grid item xs={10} md={10} lg={10}>
              <h3>Nakshatra - Create/View Scenario</h3>
              <p className="sub-title-text">
                Configure Nakshatra inputs or view an existing scenario.
              </p>
            </Grid>
            <Grid item xs={2} md={2} lg={2}>
              <CancelIcon
                className="cancel-icon"
                onClick={handleCloseWithSave}
              />
            </Grid>
          </Grid>
        </div>

        <DialogContent
          sx={{ padding: "30px!important" }}
          ref={dialogContentRef}
        >
          <Box>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={1}>
                    <Radio
                      size="small"
                      checked={selection === "create"}
                      onChange={handleSelectionChange}
                      value="create"
                      name="scenario-selection"
                      inputProps={{ "aria-label": "create" }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs="auto"
                    sx={{ display: "flex", alignItems: "center", gap: "3px" }}
                  >
                    <label>Create a new scenario</label>
                    <InfoIcon
                      fontSize="small"
                      className="info-Icon"
                      titleAccess="Define input parameters and folder details and run a new scenario."
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={1}>
                    <Radio
                      size="small"
                      checked={selection === "view"}
                      onChange={handleSelectionChange}
                      value="view"
                      name="scenario-selection"
                      inputProps={{ "aria-label": "view" }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs="auto"
                    sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <label>View an existing scenario</label>
                    <InfoIcon
                      fontSize="small"
                      className="info-Icon"
                      titleAccess="Access and review the results of an already executed scenario."
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {selection === "create" && (
            <>
              <Box sx={{ pt: 1 }}>
                <div
                  className="nakshatra-modal inner-header"
                  style={{ paddingBottom: "8px" }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      {/* <h3>Nakshatra</h3> */}
                      <div className="sub-text">
                        Add below inputs to customise the profile
                      </div>
                    </Grid>
                  </Grid>
                </div>

                <div>
                  <table
                    width={"95%"}
                    className="nakshatra-table"
                    cellPadding={0}
                    style={{ marginLeft: "20px" }}
                    cellSpacing={0}
                  >
                    <tbody>
                      <tr>
                        <td>
                          1. Are the processing and packaging plants different?
                        </td>
                        <td>
                          <div>
                            <span>
                              <Radio
                                size="small"
                                checked={
                                  isProcessingPackagingPlantsDiff === true
                                }
                                onChange={(e) => handleChangeSelectLine(e, 0)}
                                value={true}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "yes" }}
                              />
                            </span>
                            <label>Yes</label>
                            <span>
                              <Radio
                                size="small"
                                checked={
                                  isProcessingPackagingPlantsDiff === false
                                }
                                onChange={(e) => handleChangeSelectLine(e, 0)}
                                value={false}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "no" }}
                              />
                            </span>
                            <label>No</label>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>2. Shall sourcing be part of optimization?</td>
                        <td>
                          <div>
                            <span>
                              <Radio
                                size="small"
                                checked={isSourceBePartOfOptimization === true}
                                onChange={(e) => handleChangeSelectLine(e, 1)}
                                value={true}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "yes" }}
                              />
                            </span>
                            <label>Yes</label>
                            <span>
                              <Radio
                                size="small"
                                checked={isSourceBePartOfOptimization === false}
                                onChange={(e) => handleChangeSelectLine(e, 1)}
                                value={false}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "no" }}
                              />
                            </span>
                            <label>No</label>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          3. Do you want to add minor ingredient capacity?
                        </td>
                        <td>
                          <div>
                            <span>
                              <Radio
                                size="small"
                                checked={
                                  isPackingPlantAddMinorIngredient === true
                                }
                                onChange={(e) => handleChangeSelectLine(e, 2)}
                                value={true}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "yes" }}
                                disabled={!isProcessingPackagingPlantsDiff}
                              />
                            </span>
                            <label>Yes</label>
                            <span>
                              <Radio
                                size="small"
                                checked={
                                  isPackingPlantAddMinorIngredient === false
                                }
                                onChange={(e) => handleChangeSelectLine(e, 2)}
                                value={false}
                                name="radio-buttons"
                                inputProps={{ "aria-label": "no" }}
                                disabled={!isProcessingPackagingPlantsDiff}
                              />
                            </span>
                            <label>No</label>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          4. Evaluate plant closures and number of plants?
                        </td>
                        <td>
                          <div>
                            <input
                              type="checkbox"
                              onChange={handleCheckEvaluate}
                              checked={plantClosure}
                            />
                            <span style={{ paddingLeft: "8px" }}>
                              Evaluate closures
                            </span>
                          </div>
                        </td>
                      </tr>
                      {plantClosure && (
                        <>
                          <tr>
                            <td>5. Number of processing plants</td>
                            <td>
                              <TextField
                                type="number"
                                value={processingPlantsCount}
                                onChange={(e) =>
                                  setProcessingPlantsCount(e.target.value)
                                }
                                // fullWidth
                                size="small"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>6. Number of packaging plants</td>
                            <td>
                              <TextField
                                type="number"
                                value={packagingPlantsCount}
                                onChange={(e) =>
                                  setPackagingPlantsCount(e.target.value)
                                }
                                // fullWidth
                                size="small"
                              />
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </Box>

              <Box>
                <div style={{ paddingTop: "12px", paddingLeft: "20px" }}>
                  <Grid container justifyContent="left">
                    <Grid item xs={12}>
                      <h3>
                        Select or create a folder (
                        <b> Prefix : {moduleForUpload} </b>)
                      </h3>
                    </Grid>
                  </Grid>
                </div>
                <DialogContent>
                  <div style={{ marginLeft: "12px", marginRight: "12px" }}>
                    {isCreateNewFolder === true ? (
                      <>
                        <FormControl fullWidth>
                          <label className="select-folder-dropdown">
                            <h3>
                              <b> Folder Name </b>
                            </h3>
                          </label>
                          <TextField
                            id="outlined-basics"
                            value={newFolderValue}
                            variant="outlined"
                            placeholder="Folder Name"
                            onChange={handleFolderChange}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: "32px!important",
                              },
                            }}
                          />
                        </FormControl>
                        <FormControl fullWidth style={{ marginTop: "4px" }}>
                          <label className="select-folder-dropdown">
                            <h3>
                              <b> Scenario Name </b>
                            </h3>
                          </label>
                          <TextField
                            id="outlined-basic"
                            value={newScenarioValue}
                            variant="outlined"
                            placeholder="Scenario Name"
                            onChange={handleScenarioChange}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: "32px!important",
                              },
                            }}
                          />
                        </FormControl>
                      </>
                    ) : (
                      <>
                        <FormControl fullWidth style={{}}>
                          <label
                            className="select-folder-dropdown"
                            style={{ paddingTop: "0px" }}
                          >
                            <h3>Either select a folder or create new folder</h3>
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "0px",
                            }}
                          >
                            <TextField
                              id="outlined-select-currency"
                              select
                              SelectProps={{ native: true }}
                              onChange={handleChangeSelectFolder}
                              value={selectFolder?.id ?? ""}
                              size="small"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                "& .MuiOutlinedInput-root": {
                                  height: "32px!important",
                                },
                              }}
                            >
                              <option value="">Select Folder</option>
                              {folderOptions?.map((option: any, index: any) => (
                                <option key={index} value={option.id}>
                                  {option.folder}
                                </option>
                              ))}
                            </TextField>
                            {!selectFolder?.id && (
                              <a
                                onClick={handleOpenCreateFolder}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  color: "#1f36c7",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <img src={PlusIcon} alt="PlusIcon" />
                                <span>Create New Folder</span>
                              </a>
                            )}
                          </div>
                        </FormControl>
                      </>
                    )}

                    {!isCreateNewFolder && (
                      <FormControl fullWidth style={{ marginTop: "12px" }}>
                        <label className="select-folder-dropdown">
                          <h3>
                            <b> Scenario Name </b>
                          </h3>
                        </label>
                        <TextField
                          id="outlined-basic-existing"
                          value={newScenarioValue}
                          variant="outlined"
                          placeholder="Scenario Name"
                          onChange={handleScenarioChange}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "32px!important",
                            },
                          }}
                        />
                      </FormControl>
                    )}

                    {createrFolderOrScenarioErrorMsg && (
                      <>
                        <p
                          style={{
                            textAlign: "center",
                            marginTop: "4px",
                            color: "red",
                          }}
                        >
                          {createrFolderOrScenarioErrorMsg}
                        </p>
                      </>
                    )}
                    <div
                      className="create-folder"
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        className="cancel"
                        onClick={handleBackFromFolderSection}
                        disabled={!selectFolder?.id && !isCreateNewFolder}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        className="Next"
                        onClick={handleFolderProceed}
                        disabled={!isFolderAndScenarioReady}
                      >
                        Proceed
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Box>

              {createdFolderId && createdScenarioId && (
                <Box>
                  <LongTermUploadContent
                    embedded
                    isOpen={true}
                    onClose={onClose}
                    selectedFolderId={createdFolderId}
                    selectedScenarioId={createdScenarioId}
                    handleGoBack={handleGoBack}
                    selectRadio="masterData"
                    showCrossMark={false}
                    moduleKey={moduleForUpload}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {selection === "view" && (
            <div className="modal-action">
              <Button
                variant="contained"
                className="Next"
                onClick={handleProceed}
              >
                Proceed
              </Button>
            </div>
          )}
        </DialogActions>
      </BootstrapDialog>

      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.children}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default LongTermScenarioSelectionModal;
